"use client"

import { useSession } from "next-auth/react"
import type { AwsRumConfig } from "aws-rum-web"
import type { AwsRum } from "aws-rum-web"
import { createContext, useEffect, useState } from "react"
import { Session } from "@tc/ui-dfe/utils/types"

export const CloudWatchRumContext = createContext<AwsRum | null>(null)

export interface CloudWatchRumProviderProps extends AwsRumConfig {
  children: React.ReactNode
  applicationId: string
  applicationVersion: string
  identityPoolId: string
  userPoolId: string
  region: string
}

export const CloudWatchRumProvider = (props: CloudWatchRumProviderProps) => {
  const {
    children,
    applicationId,
    applicationVersion,
    identityPoolId,
    userPoolId,
    region,
    ...config
  } = props
  const [client, setClient] = useState<AwsRum>()

  const session = useSession()
  const idToken = (session?.data as Session)?.token?.id_token

  useEffect(() => {
    if (!idToken) return

    let cancelled = false

    const initRum = async () => {
      const [{ fromCognitoIdentityPool }, { AwsRum }] = await Promise.all([
        import("@aws-sdk/credential-providers"),
        import("aws-rum-web"),
      ])

      if (cancelled) return

      const credentials = fromCognitoIdentityPool({
        clientConfig: { region: region },
        identityPoolId: identityPoolId,
        logins: {
          [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken,
        },
      })
      const rum = new AwsRum(applicationId, applicationVersion, region, config)
      rum.setAwsCredentials(credentials)
      setClient(rum)
    }

    initRum()

    return () => {
      cancelled = true
    }
  }, [idToken])

  return (
    <CloudWatchRumContext.Provider value={client ?? null}>
      {children}
    </CloudWatchRumContext.Provider>
  )
}
